import React, { useState } from 'react';
import { Modal, Form, Input, Button, Alert, Typography, Space } from 'antd';
import { UserOutlined, LockOutlined, LoginOutlined } from '@ant-design/icons';
import { useAuth } from '../providers/authProvider';

const { Title, Text } = Typography;

interface LoginModalProps {
  visible: boolean;
  onClose?: () => void;
}

interface LoginFormData {
  username: string;
  password: string;
}

export const LoginModal: React.FC<LoginModalProps> = ({ visible, onClose }) => {
  const [form] = Form.useForm();
  const [error, setError] = useState<string>('');
  const { login, isLoading } = useAuth();

  const handleLogin = async (values: LoginFormData) => {
    setError('');
    
    try {
      const result = await login(values.username, values.password);
      
      if (result.success) {
        // Login erfolgreich - Modal schließen
        form.resetFields();
        if (onClose) {
          onClose();
        }
      } else {
        // Login fehlgeschlagen - Fehler anzeigen
        setError(result.error || 'Anmeldung fehlgeschlagen');
      }
    } catch (err) {
      setError('Ein unerwarteter Fehler ist aufgetreten');
      console.error('Login error:', err);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setError('');
    if (onClose) {
      onClose();
    }
  };

  return (
    <Modal
      title={null}
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={400}
      centered
      closable={false}
      maskClosable={false}
      destroyOnClose
    >
      <div style={{ padding: '20px 0' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <LoginOutlined style={{ fontSize: 48, color: '#1890ff', marginBottom: 16 }} />
          <Title level={3} style={{ margin: 0, marginBottom: 8 }}>
            Buchungsmanager
          </Title>
          <Text type="secondary">
            Bitte melden Sie sich an, um fortzufahren
          </Text>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert
            message={error}
            type="error"
            style={{ marginBottom: 24 }}
            showIcon
          />
        )}

        {/* Login Form */}
        <Form
          form={form}
          name="loginForm"
          onFinish={handleLogin}
          layout="vertical"
          size="large"
          autoComplete="off"
        >
          <Form.Item
            name="username"
            label="Benutzername"
            rules={[
              { required: true, message: 'Bitte geben Sie Ihren Benutzernamen ein' },
              { type: 'email', message: 'Bitte geben Sie eine gültige E-Mail-Adresse ein' }
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="E-Mail-Adresse"
              autoComplete="username"
            />
          </Form.Item>

          <Form.Item
            name="password"
            label="Passwort"
            rules={[
              { required: true, message: 'Bitte geben Sie Ihr Passwort ein' }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Passwort"
              autoComplete="current-password"
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={isLoading}
                style={{ width: '100%', height: 48 }}
                icon={<LoginOutlined />}
              >
                {isLoading ? 'Anmeldung läuft...' : 'Anmelden'}
              </Button>
              
              <Text type="secondary" style={{ fontSize: '12px', textAlign: 'center', display: 'block' }}>
                Verwenden Sie Ihre TYPO3 Anmeldedaten
              </Text>
            </Space>
          </Form.Item>
        </Form>

        {/* Info Section */}
        <div style={{ 
          marginTop: 32, 
          padding: 16, 
          background: '#f5f5f5', 
          borderRadius: 6,
          textAlign: 'center' 
        }}>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            <strong>Sicherheitshinweis:</strong><br />
            Diese Anwendung ist nur für autorisierte Benutzer bestimmt.
            Alle Aktivitäten werden protokolliert.
          </Text>
        </div>
      </div>
    </Modal>
  );
};

export default LoginModal; 